import type { ChangeEvent } from 'react';
import type { IControlProps, IPartialFormGroup } from '../types';
import FormControl, { type IFormErrorControlProps } from '../FormControl';
import Checkbox, { type ICheckboxProps } from '../../atoms/Checkbox';
import { useHookFormController } from '../../hooks/useHookFormController';

export type ICheckboxValueControlProps<T = unknown> = Omit<ICheckboxProps, 'checked' | 'defaultChecked' | 'value'> &
    Omit<IControlProps, 'defaultValue'> & {
        formGroupProps?: IPartialFormGroup;
        /** Значение поля формы у отмеченного флажка */
        trueValue: T;
        /** Значение поля формы у снятого флажка */
        falseValue: T;
        /**
         * Отмечен ли флажок при этом значении поля (`1` и `'1'` с сервера, регистр строки).
         * @default String(value) === String(trueValue) — то же сравнение, что и у записи значения
         */
        isChecked?: (value: unknown) => boolean;
        /**
         * Строковая подпись — разметка (очищает DOMPurify атома): подписи полей документа из конфигурации. По
         * умолчанию строка — текст: подпись из данных (свойства плагинов) не становится HTML.
         */
        labelAsHtml?: boolean;
        /**
         * Вид недоступного изменения. По умолчанию у `readOnly` и `disabled` — подсказка «Только для чтения» у подписи
         * (`hint`); `false` — без неё.
         */
        readonlyAppearance?: IFormErrorControlProps['readonlyAppearance'] | false;
        /** Новое значение поля — в тот же момент, что `onChange` флажка */
        onValueChange?: (value: T) => void;
    };

/** Значение с сервера бывает строкой при числовом `trueValue` (перечисление, бит): сравнение — по строке */
const sameValue = (value: unknown, expected: unknown) => value != null && String(value) === String(expected);

/**
 * Флажок, который пишет в поле формы не булево, а одно из двух значений (`trueValue`/`falseValue`): бит `1`/`0`,
 * строку `"True"`/`"False"`, член перечисления. Отметку показывает значение поля, поэтому `reset()` и `setValue()`
 * отражаются сразу.
 */
const CheckboxValueControl = <T,>({
    name,
    rules,
    control,
    shouldUnregister,
    formGroupProps,
    hideErrorMessage = true,
    trueValue,
    falseValue,
    isChecked,
    labelAsHtml = false,
    readonlyAppearance,
    labelClassName = 'rf-form-group__label',
    onChange,
    onValueChange,
    ...props
}: ICheckboxValueControlProps<T>) => {
    const Controller = useHookFormController();
    const appearance =
        readonlyAppearance === false
            ? undefined
            : (readonlyAppearance ?? (props.readOnly || props.disabled ? 'hint' : undefined));

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            shouldUnregister={shouldUnregister}
            render={({ field: { value, onChange: setFieldValue, ...fieldProps } }) => (
                <FormControl
                    readonlyAppearance={appearance}
                    name={name}
                    control={control}
                    {...formGroupProps}
                    hideErrorMessage={hideErrorMessage}
                >
                    <Checkbox
                        {...fieldProps}
                        {...props}
                        label={
                            labelAsHtml && typeof props.label === 'string' ? (
                                props.label
                            ) : (
                                <span className="rf-form-group-fl">{props.label}</span>
                            )
                        }
                        labelClassName={labelClassName}
                        checked={isChecked ? isChecked(value) : sameValue(value, trueValue)}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            const next = event.currentTarget.checked ? trueValue : falseValue;
                            if (sameValue(value, next)) return;
                            setFieldValue(next);
                            onChange?.(event);
                            onValueChange?.(next);
                        }}
                    />
                </FormControl>
            )}
        />
    );
};

export default CheckboxValueControl;
